<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { NButton, NPopconfirm, NSelect, NSkeleton } from 'naive-ui'
import {
  useMemoryEntriesStore,
  GLOBAL_PROJECT,
  humanizeProject,
  type MemoryEntryDetail,
} from '../stores/memory_entries'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Full-page memory entry at `/memory/:project/:name`. Renders inside
// HomeView's shell like the wiki page view does, so an entry reads like
// any other brain page instead of a sidebar drawer. Carries the
// owner-only Move + Delete actions; both return to the `/memory` list.

const route = useRoute()
const router = useRouter()
const store = useMemoryEntriesStore()
const wikiMarkdown = useWikiMarkdown()

const param = (value: string | string[] | undefined): string => (Array.isArray(value) ? value[0] : value) ?? ''
const project = computed(() => param(route.params.project))
const name = computed(() => param(route.params.name))

const detail = ref<MemoryEntryDetail | null>(null)
const loading = ref(false)
const actionError = ref<string | null>(null)
const submitting = ref(false)
const moveTarget = ref<string | null>(null)

useDocumentTitle(computed(() => detail.value?.title || detail.value?.name || null))

function scopeLabel(slug: string): string {
  return slug === GLOBAL_PROJECT ? 'Global' : humanizeProject(slug)
}

const moveOptions = computed(() => {
  const slugs = new Set<string>([GLOBAL_PROJECT])
  for (const group of store.groups) slugs.add(group.project)
  return [...slugs].filter((slug) => slug !== project.value).map((slug) => ({ value: slug, label: scopeLabel(slug) }))
})
const renderedBody = computed(() => (detail.value ? wikiMarkdown.render(detail.value.body, detail.value.path) : ''))

watch(
  [project, name],
  async ([currentProject, currentName]) => {
    detail.value = null
    actionError.value = null
    moveTarget.value = null
    if (!store.loaded) void store.fetch()
    loading.value = true
    try {
      const fetched = await store.fetchEntry(currentName, currentProject)
      if (currentProject === project.value && currentName === name.value) detail.value = fetched
    } catch (e) {
      actionError.value = e instanceof Error ? e.message : 'Failed to load entry'
    } finally {
      loading.value = false
    }
  },
  { immediate: true },
)

async function run(action: () => Promise<void>, destination: string): Promise<void> {
  submitting.value = true
  actionError.value = null
  try {
    await action()
    await router.push(destination)
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : 'Action failed'
  } finally {
    submitting.value = false
  }
}

function onDelete(): void {
  void run(() => store.deleteEntry(name.value, project.value), '/memory')
}
function onMove(): void {
  const target = moveTarget.value
  if (!target) return
  void run(
    () => store.moveEntry(name.value, project.value, target),
    `/memory/${encodeURIComponent(target)}/${encodeURIComponent(name.value)}`,
  )
}
</script>

<template>
  <article class="pt-2 pb-8 flex flex-col gap-4" data-testid="memory-entry-page">
    <RouterLink to="/memory" class="text-xs text-white/40 hover:text-white no-underline self-start">
      ← All memory
    </RouterLink>

    <p v-if="actionError" class="text-red-300 text-sm m-0" data-testid="memory-entry-error">{{ actionError }}</p>

    <div v-if="loading" class="flex flex-col gap-2" data-testid="memory-entry-loading">
      <NSkeleton text :repeat="6" />
    </div>

    <template v-else-if="detail">
      <header class="flex flex-col gap-2">
        <h1 class="text-2xl font-semibold text-white m-0" data-testid="memory-entry-title">
          {{ detail.title || detail.name }}
        </h1>
        <div class="flex flex-wrap items-center gap-2 text-xs text-white/50">
          <span class="px-2 py-0.5 rounded bg-white/[0.06]">{{ scopeLabel(detail.project) }}</span>
          <span class="uppercase tracking-widest">{{ detail.type }}</span>
        </div>
        <p v-if="detail.description" class="text-sm text-white/60 m-0">{{ detail.description }}</p>
      </header>

      <div class="wiki-markdown text-sm" data-testid="memory-entry-body" v-html="renderedBody" />

      <footer class="flex flex-col gap-3 border-t border-white/10 pt-4">
        <div class="flex items-end gap-2 max-w-md">
          <label class="flex-1 flex flex-col gap-1 text-xs text-white/50">
            Move to scope
            <NSelect
              v-model:value="moveTarget"
              size="small"
              :options="moveOptions"
              placeholder="Pick a scope…"
              data-testid="memory-move-select"
            />
          </label>
          <NButton size="small" :disabled="!moveTarget || submitting" data-testid="memory-move-btn" @click="onMove"
            >Move</NButton
          >
        </div>
        <div class="flex items-center justify-between gap-2">
          <RouterLink :to="`/p/${detail.path}`" class="text-xs text-white/40 hover:text-white no-underline">
            Open raw page →
          </RouterLink>
          <NPopconfirm @positive-click="onDelete">
            <template #trigger>
              <NButton size="small" type="error" tertiary :disabled="submitting" data-testid="memory-delete-btn">
                Delete
              </NButton>
            </template>
            Delete this memory entry? This can't be undone.
          </NPopconfirm>
        </div>
      </footer>
    </template>
  </article>
</template>
